import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useLang, tx } from "@/context/LangContext";
import { CONCERTS, DAVIDO, LOCAL_CLIPS, T } from "@/data/content";
import { FadeUp, SectionLabel, Portal, DriftTitle } from "@/components/Reveal";
import ProjectModal from "@/components/ProjectModal";
import EmptySlot from "@/components/EmptySlot";
import { Play, Mic } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const GRID = [
    { span: "md:col-span-7", ar: "aspect-video" },
    { span: "md:col-span-5", ar: "aspect-video" },
    { span: "md:col-span-5", ar: "aspect-video" },
    { span: "md:col-span-7", ar: "aspect-video" },
];

const CONCERT_GRID = [
    { span: "col-span-1 md:col-span-4", ar: "aspect-[16/10]" },
    { span: "col-span-1 md:col-span-4 md:mt-16", ar: "aspect-[16/10]" },
    { span: "col-span-1 md:col-span-4 md:mt-32", ar: "aspect-[16/10]" },
    { span: "col-span-1 md:col-span-4", ar: "aspect-[16/10]" },
];

const Thumb = ({ mv, className }) => {
    // local copy first (yt-<id>.jpg), remote YouTube URL as fallback
    const srcs = [`/img/yt-${mv.id}.jpg`, mv.thumb || mv.thumbnail, mv.poster].filter(Boolean);
    const [i, setI] = useState(0);
    if (!srcs.length) return <div className={`bg-ink2 ${className}`} />;
    return (
        <img
            src={srcs[Math.min(i, srcs.length - 1)]}
            alt={mv.title}
            loading="lazy"
            onError={() => setI((v) => v + 1)}
            className={className}
        />
    );
};

const ViewsLine = ({ views, lang }) =>
    views != null ? (
        <span className="font-mono text-[9px] tracking-[0.3em] text-gold md:text-[10px]">
            {new Intl.NumberFormat(lang === "fr" ? "fr-CH" : "en-US").format(views)}{" "}
            {lang === "fr" ? "VUES" : "VIEWS"}
        </span>
    ) : null;

const TiltCard = ({ children, className = "", testid, onClick, cursor }) => (
    <motion.div
        data-testid={testid}
        data-cursor={cursor}
        onClick={onClick}
        whileHover={{ rotateY: -6, rotateX: 4, scale: 1.015 }}
        transition={{ type: "spring", stiffness: 180, damping: 18 }}
        style={{ transformStyle: "preserve-3d", transformPerspective: 900 }}
        className={`group relative block w-full cursor-pointer overflow-hidden text-left ${className}`}
    >
        {children}
    </motion.div>
);

export default function MusicVideos() {
    const { lang } = useLang();
    const [videos, setVideos] = useState(null);
    const [failed, setFailed] = useState(false);
    const [active, setActive] = useState(null);
    const loading = videos === null && !failed;
    const featured = videos?.[0];
    const rest = videos?.slice(1) || [];

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const r = await fetch(`${API}/api/youtube/videos`);
                if (!r.ok) throw new Error("yt");
                const data = await r.json();
                const list = Array.isArray(data) ? data : data.videos || [];
                if (alive) setVideos(list);
            } catch {
                if (alive) setFailed(true);
            }
        })();
        return () => {
            alive = false;
        };
    }, []);

    return (
        <section
            id="music-videos"
            data-testid="music-videos-section"
            className="relative bg-ink py-24 text-cream md:py-36"
        >
            <div className="mx-auto max-w-[1600px] px-5 md:px-10">
                <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
                    <div>
                        <SectionLabel en={tx(lang, T.mv.label)} fr={tx(lang, T.mv.label)} />
                        <FadeUp delay={0.1}>
                            <DriftTitle dir={1}>
                                <h2 className="mt-8 font-display text-[clamp(2.6rem,8vw,7rem)] font-extrabold uppercase leading-none tracking-[-0.02em]">
                                    {tx(lang, T.mv.title)}
                                    <span className="text-violet">.</span>
                                </h2>
                            </DriftTitle>
                        </FadeUp>
                    </div>
                    <FadeUp delay={0.2}>
                        <p className="font-mono text-[9px] tracking-[0.3em] text-cream/40 md:text-[10px]">
                            {tx(lang, T.mv.note)}
                        </p>
                    </FadeUp>
                </div>

                {/* local clips — not on youtube */}
                <FadeUp delay={0.05} className="mt-12 md:mt-16">
                    <p className="mb-5 font-mono text-[9px] tracking-[0.3em] text-gold md:text-[10px]">
                        {tx(lang, T.mv.localLabel)}
                    </p>
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:gap-8">
                        {LOCAL_CLIPS.map((c, i) => (
                            <motion.div
                                key={c.id}
                                initial={{ opacity: 0, y: 40 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.15 }}
                                transition={{ duration: 0.8, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                            >
                                <TiltCard
                                    testid={`local-clip-${c.id}`}
                                    cursor="PLAY"
                                    onClick={() => setActive(c)}
                                >
                                    <div className="relative aspect-video w-full overflow-hidden">
                                        <video
                                            className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1100ms] ease-out group-hover:scale-[1.06]"
                                            poster={c.img}
                                            muted
                                            loop
                                            playsInline
                                            preload="none"
                                            onMouseEnter={(e) => {
                                                const v = e.currentTarget;
                                                v.muted = true;
                                                v.play().catch(() => {});
                                            }}
                                            onMouseLeave={(e) => e.currentTarget.pause()}
                                        >
                                            <source src={c.video} type="video/mp4" />
                                            <source src={`${c.video.split(".mp4")[0]}.webm`} type="video/webm" />
                                        </video>
                                        <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/10 to-transparent" />
                                        <span className="absolute bottom-3 right-3 bg-ink/80 px-2 py-1 font-mono text-[9px] tracking-[0.15em] text-cream">
                                            {c.duration}
                                        </span>
                                        <div className="absolute inset-x-0 bottom-0 p-5 text-left">
                                            <p className="font-mono text-[9px] tracking-[0.3em] text-violet md:text-[10px]">
                                                {c.client} — {c.year}
                                            </p>
                                            <h3 className="mt-1 font-display text-xl font-extrabold uppercase tracking-tight text-cream md:text-2xl">
                                                {c.title}
                                            </h3>
                                        </div>
                                    </div>
                                </TiltCard>
                            </motion.div>
                        ))}
                    </div>
                </FadeUp>

                {/* featured full-width */}
                <FadeUp delay={0.1} className="mt-12 md:mt-16">
                    {loading ? (
                        <div
                            data-testid="mv-loading-featured"
                            className="aspect-[16/10] w-full animate-pulse bg-cream/5 sm:aspect-[16/8] md:aspect-[21/9]"
                        />
                    ) : featured ? (
                        <TiltCard
                            testid={`mv-card-${featured.id}`}
                            cursor="PLAY"
                            onClick={() => setActive({ ...featured, youtubeId: featured.id })}
                            className="aspect-[16/10] sm:aspect-[16/8] md:aspect-[21/9]"
                        >
                            <Thumb
                                mv={featured}
                                className="absolute inset-0 h-full w-full scale-105 object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-110"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-transparent to-ink/30" />
                            {featured.duration && (
                                <span className="absolute bottom-4 right-4 bg-ink/80 px-2 py-1 font-mono text-[10px] tracking-[0.15em] text-cream md:bottom-6 md:right-6">
                                    {featured.duration}
                                </span>
                            )}
                            <div className="absolute inset-0 flex items-center justify-center">
                                <span className="relative flex h-16 w-16 items-center justify-center rounded-full border border-cream/50 bg-ink/30 backdrop-blur-sm transition-transform duration-500 group-hover:scale-110 md:h-20 md:w-20">
                                    <span className="absolute inset-0 animate-pulse-ring rounded-full border border-violet/70" />
                                    <Play size={18} className="ml-1 fill-cream text-cream" />
                                </span>
                            </div>
                            <div className="absolute inset-x-0 bottom-0 flex items-end justify-between p-5 md:p-9">
                                <div className="pr-20 text-left md:pr-28">
                                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                                        <p className="font-mono text-[9px] tracking-[0.3em] text-cream/70 md:text-[10px]">
                                            {featured.client}
                                            {featured.year && ` — ${featured.year}`}
                                        </p>
                                        <ViewsLine views={featured.views} lang={lang} />
                                    </div>
                                    <h3 className="mt-2 font-display text-2xl font-extrabold uppercase tracking-tight md:text-5xl">
                                        {featured.title}
                                    </h3>
                                </div>
                            </div>
                        </TiltCard>
                    ) : (
                        <EmptySlot
                            testid="mv-empty-featured"
                            icon={Play}
                            label={tx(lang, T.empty.mv)}
                            aspect="aspect-[16/10] sm:aspect-[16/8] md:aspect-[21/9]"
                            dark
                        />
                    )}
                </FadeUp>

                {/* asymmetric grid */}
                <div className="mt-8 grid grid-cols-1 gap-6 md:mt-12 md:grid-cols-12 md:gap-8">
                    {loading ? (
                        GRID.slice(0, 3).map((G, i) => (
                            <div
                                key={`mv-skel-${i}`}
                                data-testid={`mv-loading-${i + 1}`}
                                className={`${G.span} ${G.ar.split(" ")[0]} animate-pulse bg-cream/5`}
                            />
                        ))
                    ) : failed || !videos || videos.length === 0 ? (
                        GRID.slice(0, 3).map((G, i) => (
                            <div key={`mv-empty-${i}`} className={G.span}>
                                <EmptySlot
                                    testid={`mv-empty-${i + 1}`}
                                    icon={Play}
                                    label={tx(lang, T.empty.mv)}
                                    aspect={G.ar.split(" ")[0]}
                                    dark
                                    index={i + 2}
                                />
                            </div>
                        ))
                    ) : (
                        rest.map((mv, i) => {
                            const G = GRID[i % GRID.length];
                            return (
                                <motion.div
                                    key={mv.id}
                                    initial={{ opacity: 0, y: 40 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, amount: 0.15 }}
                                    transition={{
                                        duration: 0.8,
                                        delay: (i % 2) * 0.08,
                                        ease: [0.16, 1, 0.3, 1],
                                    }}
                                    className={G.span}
                                >
                                    <TiltCard
                                        testid={`mv-card-${mv.id}`}
                                        cursor="PLAY"
                                        onClick={() => setActive({ ...mv, youtubeId: mv.id })}
                                    >
                                        <div className={`relative w-full overflow-hidden ${G.ar}`}>
                                            <Thumb
                                                mv={mv}
                                                className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1100ms] ease-out group-hover:scale-[1.07] group-hover:-translate-y-1"
                                            />
                                            <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/5 to-transparent opacity-90 transition-opacity duration-500 group-hover:opacity-100" />
                                            {mv.duration && (
                                                <span className="absolute bottom-3 right-3 bg-ink/80 px-2 py-1 font-mono text-[9px] tracking-[0.15em] text-cream">
                                                    {mv.duration}
                                                </span>
                                            )}
                                            <div className="absolute inset-x-0 bottom-0 flex items-end justify-between p-5 pr-16 md:p-7">
                                                <div className="translate-y-3 text-left transition-transform duration-500 group-hover:translate-y-0">
                                                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                                        <p className="font-mono text-[9px] tracking-[0.3em] text-violet md:text-[10px]">
                                                            {mv.client}
                                                            {mv.year && ` — ${mv.year}`}
                                                        </p>
                                                        <ViewsLine views={mv.views} lang={lang} />
                                                    </div>
                                                    <h3 className="mt-2 font-display text-xl font-extrabold uppercase tracking-tight text-cream md:text-2xl">
                                                        {mv.title}
                                                    </h3>
                                                </div>
                                                <span className="translate-y-3 opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                                                    <Play size={16} className="fill-cream text-cream" />
                                                </span>
                                            </div>
                                        </div>
                                    </TiltCard>
                                </motion.div>
                            );
                        })
                    )}
                </div>

                {/* davido — featured live block */}
                <div id="davido-block" className="mt-20 md:mt-28" data-testid="davido-block">
                    <FadeUp>
                        <div className="flex flex-col justify-between gap-4 border-t border-white/10 pt-10 md:flex-row md:items-end">
                            <h3 className="font-display text-3xl font-extrabold uppercase tracking-tight md:text-5xl">
                                {tx(lang, T.davido.title)}
                                <span className="text-violet">.</span>
                                <span data-testid="davido-powered" className="mt-1 block font-mono text-[9px] font-normal lowercase tracking-[0.3em] text-gold/70 md:mt-2 md:text-[10px]">
                                    powered by abizway
                                </span>
                            </h3>
                            <p className="font-mono text-[9px] tracking-[0.3em] text-cream/40 md:text-[10px]">
                                {tx(lang, T.davido.note)}
                            </p>
                        </div>
                    </FadeUp>

                    {/* featured long video */}
                    <FadeUp delay={0.05} className="mt-8">
                        <TiltCard
                            testid="davido-featured"
                            cursor="PLAY"
                            onClick={() => setActive(DAVIDO.featured)}
                            className="aspect-[16/10] sm:aspect-[16/8] md:aspect-[21/9]"
                        >
                            <img
                                src={DAVIDO.featured.img}
                                alt={DAVIDO.featured.title}
                                loading="lazy"
                                className="absolute inset-0 h-full w-full scale-105 object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-110"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-transparent to-ink/30" />
                            <span className="absolute bottom-4 right-4 bg-ink/80 px-2 py-1 font-mono text-[10px] tracking-[0.15em] text-cream md:bottom-6 md:right-6">
                                {DAVIDO.featured.duration}
                            </span>
                            <div className="absolute inset-0 flex items-center justify-center">
                                <span className="relative flex h-16 w-16 items-center justify-center rounded-full border border-gold/60 bg-ink/30 backdrop-blur-sm transition-transform duration-500 group-hover:scale-110 md:h-20 md:w-20">
                                    <span className="absolute inset-0 animate-pulse-ring rounded-full border border-gold/70" />
                                    <Play size={18} className="ml-1 fill-cream text-cream" />
                                </span>
                            </div>
                            <div className="absolute inset-x-0 bottom-0 p-5 text-left md:p-9">
                                <p className="font-mono text-[9px] tracking-[0.3em] text-gold md:text-[10px]">
                                    {DAVIDO.featured.client} — {DAVIDO.featured.year}
                                </p>
                                <h4 className="mt-2 font-display text-2xl font-extrabold uppercase tracking-tight md:text-5xl">
                                    {DAVIDO.featured.title}
                                </h4>
                            </div>
                        </TiltCard>
                    </FadeUp>

                    {/* reels row */}
                    <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-3 md:gap-8">
                        {DAVIDO.reels.map((r, i) => (
                            <motion.div
                                key={r.id}
                                initial={{ opacity: 0, y: 40 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.15 }}
                                transition={{ duration: 0.8, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                            >
                                <TiltCard
                                    testid={`davido-reel-${r.id}`}
                                    cursor="PLAY"
                                    onClick={() => setActive(r)}
                                >
                                    <div className="relative aspect-video w-full overflow-hidden">
                                        <video
                                            className="absolute inset-0 h-full w-full object-cover"
                                            poster={r.img}
                                            muted
                                            loop
                                            playsInline
                                            preload="none"
                                            onMouseEnter={(e) => {
                                                const v = e.currentTarget;
                                                v.muted = true;
                                                v.play().catch(() => {});
                                            }}
                                            onMouseLeave={(e) => e.currentTarget.pause()}
                                        >
                                            <source src={r.video} type="video/mp4" />
                                            <source src={`${r.video.split(".mp4")[0]}.webm`} type="video/webm" />
                                        </video>
                                        <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent" />
                                        <span className="absolute bottom-3 right-3 bg-ink/80 px-2 py-1 font-mono text-[9px] tracking-[0.15em] text-cream">
                                            {r.duration}
                                        </span>
                                        <div className="absolute inset-x-0 bottom-0 p-4 text-left">
                                            <p className="font-mono text-[9px] tracking-[0.3em] text-gold">{r.client}</p>
                                            <h4 className="mt-1 font-display text-base font-extrabold uppercase tracking-tight text-cream">
                                                {r.title}
                                            </h4>
                                        </div>
                                    </div>
                                </TiltCard>
                            </motion.div>
                        ))}
                    </div>

                    {/* concert photos */}
                    <div className="mt-12 grid grid-cols-2 gap-4 md:grid-cols-12 md:gap-8">
                        {CONCERTS.map((c, i) => {
                            const L = CONCERT_GRID[i % CONCERT_GRID.length];
                            return (
                                <motion.button
                                    key={c.id}
                                    data-testid={`concert-card-${c.id}`}
                                    data-cursor="PLAY"
                                    onClick={() =>
                                        setActive(DAVIDO.reels[i] || DAVIDO.featured)
                                    }
                                    initial={{ opacity: 0, y: 40 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, amount: 0.15 }}
                                    transition={{ duration: 0.8, delay: (i % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
                                    className={`group relative block w-full overflow-hidden ${L.span}`}
                                >
                                    <div className={`relative w-full overflow-hidden ${L.ar}`}>
                                        <img
                                            src={c.img}
                                            alt={c.title}
                                            loading="lazy"
                                            className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1100ms] ease-out group-hover:scale-[1.05]"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                                    </div>
                                    <p className="mt-3 flex items-center justify-between text-left font-mono text-[9px] tracking-[0.25em] text-cream/50 md:text-[10px]">
                                        <span>
                                            {c.title}
                                            <span className="text-violet"> — {c.client}</span>
                                        </span>
                                        <span>{c.year}</span>
                                    </p>
                                </motion.button>
                            );
                        })}
                    </div>
                </div>
            </div>

            <Portal><AnimatePresence>
                {active && (
                    <ProjectModal project={active} onClose={() => setActive(null)} />
                )}
            </AnimatePresence></Portal>
        </section>
    );
}
